import {
    Component,
    Input,
    computed,
    effect,
    inject,
    signal
} from "@angular/core";
import { ChatNandaService } from "@app/shared/services/chat-nanda.service";
import { DeviceService } from "@app/shared/services/device.service";
import { ThemesCustom } from "@app/views/auth/report/components/report-performance/report-performance.theme";
import {
    BALANCE_SERIES_IDS,
    RECEITA_BRUTA,
    RECEITA_LIQUIDA
} from "@arch/_shared/constants";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { SerieReferenceType } from "@arch/_shared/types/source-reference.types";
import { DateUtils } from "@arch/_shared/utils/date.utils";
import { ThemeService } from "@arch/domain/application/services/theme.service";
import {
    CompanyEntity,
    ReportEntity,
    SerieEntity,
    VisualizationsBatchEntity
} from "@arch/domain/entity";
import {
    GetDreDataController,
    GetPerformanceDataController,
    GetSeriesAccountsPlanController,
    GetSeriesComparationResult,
    GetSeriesGoalsModeledController,
    GetSeriesRepositoryController,
    GetVisualizationDreController
} from "@arch/presentation/controllers";
import { CardIndicatorPerformanceComponent } from "@shared/components/card-indicator-performance/card-indicator-performance.component";
import { SharedDsnModule } from "@shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@shared/modules/shared-material.module";
import { DialogService } from "@shared/services/dialog/dialog.service";
import _ from "lodash";
import { ChartPerformanceReportComponent } from "../chart-performance-report/chart-performance-report.component";

@Component({
    selector: "app-report-performance",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        ChartPerformanceReportComponent,
        CardIndicatorPerformanceComponent
    ],
    templateUrl: "./report-performance.component.html",
    styleUrl: "./report-performance.component.scss"
})
export class ReportPerformanceComponent {
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) visualizations: VisualizationsBatchEntity;
    @Input({ required: true }) set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    public readonly optionsSignal = signal<VisualizationOptionsType>(
        {} as VisualizationOptionsType
    );
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) previewMode: boolean;
    @Input({ required: false }) pdfMode: boolean;
    @Input({ required: false }) linkDigital: string = "";

    private readonly themeService = inject(ThemeService);
    private readonly deviceService = inject(DeviceService);
    private readonly dialogService = inject(DialogService);
    private readonly chatNandaService = inject(ChatNandaService);

    private readonly getPerformanceDataController = inject(
        GetPerformanceDataController
    );
    private readonly getSeriesComparationResultController = inject(
        GetSeriesComparationResult
    );
    private readonly getVisualizationDreController = inject(
        GetVisualizationDreController
    );
    private readonly getSeriesGoalsModeledController = inject(
        GetSeriesGoalsModeledController
    );
    private readonly getDreDataController = inject(GetDreDataController);
    private readonly getSeriesRepositoryController = inject(
        GetSeriesRepositoryController
    );
    private readonly getSeriesAccountsPlanController = inject(
        GetSeriesAccountsPlanController
    );
    public readonly chatNandaIsOpen = this.chatNandaService.isOpened;
    public readonly isMobile = this.deviceService.getIsMobile();

    private readonly seriesPerformanceSignal = signal<SeriesData>({
        good: [],
        stable: [],
        bad: []
    });

    private readonly seriesComparationsSignal = signal<SeriesData>({
        good: [],
        stable: [],
        bad: []
    });

    private readonly seriesBudgetSignal = signal<SeriesData>({
        good: [],
        stable: [],
        bad: []
    });

    private readonly seriesGoalsSignal = signal<GoalsSeriesData>({
        good: [],
        bad: []
    });

    private readonly RESULT_CONFIGS: Record<
        TabType,
        ResultPerformanceConfig[]
    > = {
        reference: [
            {
                icon: "fa-solid fa-circle",
                label: "Acima do valor de referência",
                color: this.getThemeCustomColor("cyan-primary"),
                type: "good",
                typeColor: "good",
                progressColor: this.getThemeCustomColor("cyan-primary")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Dentro do valor de referência",
                color: this.getThemeCustomColor("yellow-dark"),
                type: "stable",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("yellow-dark")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Abaixo do valor de referência",
                color: this.getThemeCustomColor("purple-dark"),
                type: "bad",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("purple-dark")
            }
        ],
        comparison: [
            {
                icon: "fa-solid fa-circle",
                label: "Melhorou",
                color: this.getThemeCustomColor("cyan-primary"),
                type: "good",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("cyan-primary")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Estabilizou",
                color: this.getThemeCustomColor("yellow-dark"),
                type: "stable",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("yellow-dark")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Piorou",
                color: this.getThemeCustomColor("purple-dark"),
                type: "bad",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("purple-dark")
            }
        ],
        budget: [
            {
                icon: "fa-solid fa-circle",
                label: "Resultado bom",
                color: this.getThemeCustomColor("cyan-primary"),
                type: "good",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("cyan-primary")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Resultado estável",
                color: this.getThemeCustomColor("yellow-dark"),
                type: "stable",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("yellow-dark")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Resultado ruim",
                color: this.getThemeCustomColor("purple-dark"),
                type: "bad",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("purple-dark")
            }
        ],
        goals: [
            {
                icon: "fa-solid fa-circle",
                label: "Objetivo alcançado",
                color: this.getThemeCustomColor("cyan-primary"),
                type: "good",
                typeColor: "good",
                progressColor: this.getThemeCustomColor("cyan-primary")
            },
            {
                icon: "fa-solid fa-circle",
                label: "Não alcançados",
                color: this.getThemeCustomColor("purple-dark"),
                type: "bad",
                typeColor: "default",
                progressColor: this.getThemeCustomColor("purple-dark")
            }
        ]
    };

    public readonly tabsSignal = signal<TabConfig[]>([]);
    public readonly currentTabSignal = signal<TabType>("reference");
    public readonly resultDataSignal = signal<ResultPerformanceType[]>([]);

    public readonly resultsReferencesSignal = computed(() =>
        this.buildResultsFromSeries(this.seriesPerformanceSignal(), "reference")
    );

    public readonly resultsComparationsSignal = computed(() =>
        this.buildResultsFromSeries(
            this.seriesComparationsSignal(),
            "comparison"
        )
    );

    public readonly resultsBudgetSignal = computed(() =>
        this.buildResultsFromSeries(this.seriesBudgetSignal(), "budget")
    );

    public readonly resultsGoalsSignal = computed(() =>
        this.buildResultsFromSeries(this.seriesGoalsSignal(), "goals")
    );

    public readonly comparationLabel = computed(() => {
        if (!this.optionsSignal()) return "";
        return DateUtils.formatByPeriodName(
            this.optionsSignal().comparation.period,
            this.optionsSignal().comparation.date
        );
    });

    constructor() {
        effect(async () => {
            try {
                const options = this.optionsSignal();

                if (!options || !this.company) return;
                this.initializeTabs(options);
                await this.loadAllSeriesData(options);
                this.updateResultData();
            } catch (error) {
                console.error("Erro ao carregar dados de performance:", error);
            }
        });
    }

    private initializeTabs(options: VisualizationOptionsType): void {
        const comparationLabel = DateUtils.formatByPeriodName(
            options.comparation.period,
            options.comparation.date
        );
        const tabs: TabConfig[] = [
            { label: "Referência", value: "reference" },
            { label: comparationLabel, value: "comparison" }
        ];

        if (options.budget) {
            tabs.push({ label: "Orçamento", value: "budget" });
        }
        if (options.goals) {
            tabs.push({ label: "Objetivos", value: "goals" });
        }
        this.tabsSignal.set(tabs);
        this.currentTabSignal.set(tabs[0].value);
    }

    private async loadAllSeriesData(
        options: VisualizationOptionsType
    ): Promise<void> {
        const allSeriesIds = await this.collectAllSeriesIds(options);
        const series = this.getSeriesRepositoryController.execute([
            ...new Set(allSeriesIds)
        ]);
        await Promise.all([
            this.loadPerformanceData(options, series),
            this.loadComparationData(options, series),
            this.loadBudgetData(options, series),
            this.loadGoalsData(options, series)
        ]);
    }

    private async collectAllSeriesIds(
        options: VisualizationOptionsType
    ): Promise<string[]> {
        const allSeriesIds =
            this.visualizations
                ?.getAllSeriesIdsWithSource(
                    null,
                    this.report.visibleVisualizations
                )
                .map((s) => s._id) || [];
        if (["budget", "projection"].includes(this.report.type)) {
            const series = this.getSeriesAccountsPlanController.execute(
                this.report.options.source,
                this.company.props._id
            );
            allSeriesIds.push(...series.map((s) => s.props._id));
        }
        if (options.balance) {
            allSeriesIds.push(...BALANCE_SERIES_IDS);
        }
        if (options.dre) {
            const dreSeriesIds = await this.getDreSeriesIds(options);
            allSeriesIds.push(...dreSeriesIds);
        }
        return allSeriesIds;
    }

    private async getDreSeriesIds(
        options: VisualizationOptionsType
    ): Promise<string[]> {
        if (options.source === "accounting") {
            const dreVisualization = this.getVisualizationDreController.execute(
                {
                    sources: [options.source],
                    type: "dre",
                    companyId: this.company.props._id
                }
            );
            return [
                ...(dreVisualization?.props.series || []),
                RECEITA_BRUTA,
                RECEITA_LIQUIDA
            ];
        } else {
            const seriesDRE = await this.getDreDataController.execute({
                options: options,
                company: this.company,
                dates: [],
                optionsConfig: {
                    verticalAnalysisReference: "RECEITA BRUTA",
                    calcAnalysisVertical: false,
                    calcAnalysisHorizontal: false
                }
            });
            return seriesDRE.map((s) => s.serie.props._id);
        }
    }

    private async loadPerformanceData(
        options: VisualizationOptionsType,
        series: SerieEntity[]
    ): Promise<void> {
        const performanceOptions = { ...options, budget: "" };
        const seriesPerformance = this.getPerformanceDataController.execute({
            sources: options.sources,
            company: this.company,
            series,
            options: performanceOptions
        });
        if (!this.hasPerformanceData(seriesPerformance)) {
            const tabs = this.tabsSignal().filter(
                (tab) => tab.value !== "reference"
            );
            this.tabsSignal.set(tabs);
            if (tabs.length > 0) {
                this.currentTabSignal.set(tabs[0].value);
            }
        }
        this.seriesPerformanceSignal.set(seriesPerformance);
    }

    private async loadComparationData(
        options: VisualizationOptionsType,
        series: SerieEntity[]
    ): Promise<void> {
        const comparationOptions = { ...options, budget: "" };
        const seriesComparations =
            this.getSeriesComparationResultController.execute({
                company: this.company,
                series,
                options: comparationOptions,
                extOptions: {
                    excludeZeroValues: true
                }
            });
        this.seriesComparationsSignal.set(seriesComparations);
    }

    private async loadBudgetData(
        options: VisualizationOptionsType,
        series: SerieEntity[]
    ): Promise<void> {
        if (options.budget) {
            const seriesBudget = this.getPerformanceDataController.execute({
                sources: options.sources,
                company: this.company,
                series,
                options
            });
            if (!this.hasPerformanceData(seriesBudget)) {
                const tabs = this.tabsSignal().filter(
                    (tab) => tab.value !== "budget"
                );
                this.tabsSignal.set(tabs);
                if (tabs.length > 0) {
                    this.currentTabSignal.set(tabs[0].value);
                }
            }
            this.seriesBudgetSignal.set(seriesBudget);
        } else {
            this.seriesBudgetSignal.set({ good: [], stable: [], bad: [] });
        }
    }

    private async loadGoalsData(
        options: VisualizationOptionsType,
        series: SerieEntity[]
    ): Promise<void> {
        if (options.goals) {
            const seriesGoals = this.getSeriesGoalsModeledController.execute({
                series: this.getSeriesRepositoryController.execute([], null, {
                    all: true
                }),
                seriesInUse: series,
                options
            });
            this.seriesGoalsSignal.set(seriesGoals);
        } else {
            this.seriesGoalsSignal.set({ good: [], bad: [] });
        }
    }

    private hasPerformanceData(seriesPerformance: SeriesData): boolean {
        return (
            seriesPerformance.good.length > 0 ||
            seriesPerformance.stable.length > 0 ||
            seriesPerformance.bad.length > 0
        );
    }

    private buildResultsFromSeries(
        seriesData: SeriesData | GoalsSeriesData,
        tabType: TabType
    ): ResultPerformanceType[] {
        const configs = this.RESULT_CONFIGS[tabType];

        return configs.map((config) => {
            const series: SerieReferenceType[] =
                (seriesData as any)[config.type] || [];

            return {
                ...config,
                series,
                labelHint: this.buildLabelHint(series.length)
            };
        });
    }

    private buildLabelHint(count: number): string {
        const plural = count !== 1 ? "es" : "";
        return `${count} indicador${plural}`;
    }

    private updateResultData(): void {
        const currentTab = this.currentTabSignal();
        const tabResultsMap: Record<TabType, () => ResultPerformanceType[]> = {
            reference: () => this.resultsReferencesSignal(),
            comparison: () => this.resultsComparationsSignal(),
            budget: () => this.resultsBudgetSignal(),
            goals: () => this.resultsGoalsSignal()
        };

        const results = tabResultsMap[currentTab]?.() || [];
        this.resultDataSignal.set(results);
    }

    public onClickInfo(serie: SerieReferenceType): void {
        if (serie.associatedIndicator) {
            this.dialogService.info({
                title: "Valor de referência",
                description: `A referência considera a proporção do ${serie.name} em relação à ${serie.associatedIndicator}.`
            });
        }
    }

    public onChangeTab(tab: TabType): void {
        this.currentTabSignal.set(tab);
        this.updateResultData();
    }

    public openInfoDialog(): void {
        this.onOpenHelpCenter();
        this.dialogService.info({
            title: "Valor de referência",
            description:
                "Os valores de referência provêm de pesquisas acadêmicas, estudos, consultorias e prática de mercado, baseados em amostras de empresas de diversos setores e tamanhos."
        });
    }

    public orderSeriesByName(
        series: SerieReferenceType[]
    ): SerieReferenceType[] {
        return _.orderBy(series, ["name"], ["asc"]);
    }

    private getThemeCustomColor(color: string) {
        const themename = this.themeService.getThemeName();
        return ThemesCustom[themename]?.[color] || "";
    }

    public onOpenHelpCenter(): void {
        window.open(
            "https://ajuda.nucont.com/centraldeconhecimento/de-onde-vêm-os-valores-de-referência-da-performance",
            "_blank"
        );
    }
}

type PerformanceResultType = "good" | "stable" | "bad";
type TabType = "reference" | "comparison" | "budget" | "goals";

interface TabConfig {
    label: string;
    value: TabType;
}

export interface ResultPerformanceConfig {
    icon: string;
    label: string;
    color: string;
    type: PerformanceResultType;
    typeColor: "default" | PerformanceResultType;
    progressColor: string;
}

export interface ResultPerformanceType extends ResultPerformanceConfig {
    series: SerieReferenceType[];
    labelHint: string;
}

interface SeriesData {
    good: SerieReferenceType[];
    stable: SerieReferenceType[];
    bad: SerieReferenceType[];
}

interface GoalsSeriesData {
    good: SerieReferenceType[];
    bad: SerieReferenceType[];
}
